type Callback = (errorOrNull: Error | null, script: HTMLScriptElement) => void;

type AllowedAttributes = 'type' | 'charset' | 'async' | 'text' | 'defer';

type Options = Partial<Pick<HTMLScriptElement, AllowedAttributes>> & {
  attrs?: Record<string, string>;
};

export const loadScript = (src: HTMLScriptElement['src'], opts: Options, cb: Callback) => {
  const head = document.head || document.getElementsByTagName('head')[0];
  const script = document.createElement('script');

  if (typeof opts === 'function') {
    cb = opts;
    opts = {};
  }

  opts = opts || {};
  cb = cb || function () {};

  const existingScript = Array.from(document.scripts).find((s) => s.src === src);

  if (existingScript) {
    if ((existingScript as HTMLScriptElement & { loaded?: boolean }).loaded) {
      cb(null, existingScript);
    } else {
      existingScript.addEventListener('load', () => cb(null, existingScript));
      existingScript.addEventListener('error', () => cb(new Error('Script failed to load: ' + src), existingScript));
    }
  }

  script.type = opts.type || 'text/javascript';
  script.async = 'async' in opts ? !!opts.async : true;
  script.src = src;

  if (opts.attrs) {
    setAttributes(script, opts.attrs);
  }

  if (opts.text) {
    script.text = '' + opts.text;
  }

  const onend = stdOnEnd;
  onend(script, cb);

  // some good legacy browsers (firefox) fail the 'in' detection above
  // so as a fallback always set onload
  // old IE will ignore this and new IE will set onload
  if (!script.onload) {
    stdOnEnd(script, cb);
  }

  head.appendChild(script);
};

function setAttributes(script: HTMLScriptElement, attrs: Options['attrs']) {
  for (const attr in attrs) {
    script.setAttribute(attr, attrs[attr]);
  }
}

function stdOnEnd(script: HTMLScriptElement, cb: Callback) {
  script.onload = function () {
    this.onerror = this.onload = null;
    cb(null, script);
  };

  script.onerror = function () {
    // this.onload = null here is necessary
    // because even IE9 works not like others
    this.onerror = this.onload = null;
    cb(new Error('Failed to load ' + this.src), script);
  };
}
